import React from 'react';
import styled from 'styled-components';
import { IndicatorSectionKey, stageIdToString } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faCircleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

export const getEmptyIndicatorStageText = (
  sectionKey: IndicatorSectionKey,
) => `If you don't add any indicators, your model will not include the ${stageIdToString(
  sectionKey,
)} stage in your final logic model. This is
totally fine since it's an optional stage, but you indicated earlier
in the process that behavior change was a part of your program so we
want to flag this change!`;

interface StageEmptyStateProps {
  maxWidth: number;
  sectionKey: IndicatorSectionKey;
}

const StyledDiv = styled.div<{
  maxWidth: number;
  iconColor?: keyof typeof COLORS;
}>`
  max-width: ${({ maxWidth }) => maxWidth}px;
  margin-top: 40px;
  margin-right: auto;
  margin-left: auto;
  .icon {
    display: block;
    margin-right: auto;
    margin-left: auto;
    margin-bottom: 14px;
    ${({ iconColor }) => (iconColor ? `color: ${COLORS[iconColor]};` : '')}
  }
`;

const MessageWithIcon: React.FC<{
  maxWidth: number;
  iconColor?: keyof typeof COLORS;
  icon: IconProp | IconDefinition;
  children: React.ReactNode | React.ReactNode[];
}> = ({ icon, maxWidth, iconColor, children }) => {
  return (
    <StyledDiv maxWidth={maxWidth} iconColor={iconColor}>
      <FontAwesomeIcon icon={icon} size="2x" className="icon" />
      {children}
    </StyledDiv>
  );
};

const StageEmptyState: React.FC<StageEmptyStateProps> = ({
  sectionKey,
  maxWidth,
}) => {
  switch (sectionKey) {
    case 'capacityDevelopment':
    case 'learn':
    case 'act':
      return (
        <MessageWithIcon
          maxWidth={maxWidth}
          icon={faTriangleExclamation}
          iconColor="warning"
        >
          {getEmptyIndicatorStageText(sectionKey)}
        </MessageWithIcon>
      );
    case 'reach':
    case 'succeed':
      return (
        <MessageWithIcon
          iconColor="critical"
          maxWidth={maxWidth}
          icon={faCircleExclamation}
        >
          You haven’t selected any indicators.
          <br />
          Please make sure you do by clicking the add button below.
        </MessageWithIcon>
      );

    default:
      return null;
  }
};

export default StageEmptyState;
